import type { RJSFSchema, UiSchema } from '@rjsf/utils';

/**
 * 서버에서 내려오는 커스텀 폼 필드 타입
 */
interface ServerFormField {
  key: string;
  type: 'TEXT' | 'DATE' | 'SELECT' | 'NUMBER' | 'BOOLEAN';
  helpText?: string;
  labelKey?: string;
  required?: boolean;
  displayOrder?: number;
  options?: { label: string; value: string }[];
}

interface ServerFormSchema {
  fields: ServerFormField[];
}

interface ConvertedFormSchema {
  schema: RJSFSchema;
  uiSchema: UiSchema;
  /** 필드별 validation 에러 메시지 (key → helpText) */
  fieldHelpTexts: Record<string, string>;
}

const FIELD_TYPE_MAP: Record<ServerFormField['type'], () => Record<string, unknown>> = {
  TEXT: () => ({ type: 'string' }),
  DATE: () => ({ type: 'string', format: 'date' }),
  NUMBER: () => ({ type: 'number' }),
  BOOLEAN: () => ({ type: 'boolean' }),
  SELECT: () => ({ type: 'string' }),
};

/**
 * 서버 커스텀 폼 스키마를 RJSF JSON Schema + UiSchema로 변환
 * - key → 라벨(title)
 * - labelKey → placeholder
 * - helpText → validation 에러 메시지 (transformErrors를 통해 빨간색으로 표시)
 */
export const convertFormSchema = (raw: string, t: (key: string) => string): ConvertedFormSchema | null => {
  try {
    const parsed = JSON.parse(raw) as ServerFormSchema;
    if (!parsed.fields?.length) return null;

    const sortedFields = [...parsed.fields].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    const properties: Record<string, Record<string, unknown>> = {};
    const required: string[] = [];
    const uiOrder: string[] = [];
    const uiSchemaFields: Record<string, Record<string, unknown>> = {};
    const fieldHelpTexts: Record<string, string> = {};

    sortedFields.forEach(field => {
      const typeFactory = FIELD_TYPE_MAP[field.type];
      if (!typeFactory) return;

      const fieldSchema: Record<string, unknown> = {
        ...typeFactory(),
        title: field.key,
      };

      // SELECT 타입: enum + enumNames 방식
      if (field.type === 'SELECT' && field.options?.length) {
        fieldSchema.enum = field.options.map(opt => opt.value);
        fieldSchema.enumNames = field.options.map(opt => opt.label);
      }

      properties[field.key] = fieldSchema;
      uiOrder.push(field.key);

      // 필드별 uiSchema 설정
      const fieldUi: Record<string, unknown> = {};

      // BOOLEAN 타입: 체크박스 위젯 명시
      if (field.type === 'BOOLEAN') {
        fieldUi['ui:widget'] = 'checkbox';
      }

      // labelKey → placeholder (SELECT는 ui:options로 전달)
      if (field.labelKey) {
        if (field.type === 'SELECT') {
          fieldUi['ui:options'] = { placeholderText: t(field.labelKey) };
        } else if (field.type !== 'BOOLEAN') {
          fieldUi['ui:placeholder'] = t(field.labelKey);
        }
      }

      if (Object.keys(fieldUi).length > 0) {
        uiSchemaFields[field.key] = fieldUi;
      }

      // helpText → validation 에러 메시지
      if (field.helpText) {
        fieldHelpTexts[field.key] = t(field.helpText);
      }

      if (field.required) {
        required.push(field.key);
      }
    });

    const schema: RJSFSchema = {
      type: 'object',
      properties,
      ...(required.length > 0 && { required }),
    };

    const uiSchema: UiSchema = {
      'ui:order': uiOrder,
      ...uiSchemaFields,
    };

    return { schema, uiSchema, fieldHelpTexts };
  } catch {
    return null;
  }
};
